import React, { useState } from 'react';
import { ArrowLeft, Mail, Check, AlertCircle, Loader } from 'lucide-react';
import { authApi } from '../api/client';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF', red:'#DC2626', green:'#16A34A',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

const ForgotPassword: React.FC = () => {
  const [email,   setEmail]   = useState('');
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address.'); return; }
    setError(''); setLoading(true);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (err: any) {
      // Still show success to prevent email enumeration
      // but log the error for debugging
      console.error('Forgot password error:', err);
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'DM Sans',sans-serif;background:${C.surface};color:${C.ink};}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, background:`linear-gradient(160deg,#FFFDF8 60%,#FFF6E0 100%)` }}>
        <div style={{ width:'100%', maxWidth:420, animation:'fadeUp .4s ease both' }}>

          <a href="/login" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:C.muted, textDecoration:'none', marginBottom:36, fontFamily:font.body, fontWeight:500 }}>
            <ArrowLeft size={14}/> Back to login
          </a>

          <div style={{ background:C.white, borderRadius:20, border:`1px solid ${C.border}`, padding:'36px', boxShadow:'0 8px 32px rgba(0,0,0,0.06)' }}>
            {!sent ? (
              <>
                <div style={{ width:52, height:52, borderRadius:16, background:C.amber100, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:22 }}>
                  <Mail size={22} color={C.amber400}/>
                </div>
                <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:26, letterSpacing:'-0.04em', color:C.ink, marginBottom:8 }}>
                  Forgot your password?
                </h1>
                <p style={{ fontSize:14, color:C.muted, lineHeight:1.65, marginBottom:28, fontFamily:font.body }}>
                  Enter the email address you signed up with and we'll send you a reset link.
                </p>

                <form onSubmit={handleSubmit} noValidate style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  <div>
                    <label style={{ fontSize:12, fontWeight:600, color:C.inkLight, fontFamily:font.body, display:'block', marginBottom:6 }}>
                      Email address
                    </label>
                    <input
                      type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com" autoComplete="email"
                      style={{ width:'100%', height:46, padding:'0 14px', borderRadius:10, border:`1.5px solid ${error ? C.red : C.border}`, fontFamily:font.body, fontSize:15, color:C.ink, outline:'none', background: error ? '#FEF2F2' : C.white }}
                    />
                    {error && (
                      <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:6 }}>
                        <AlertCircle size={12} color={C.red}/>
                        <span style={{ fontSize:12, color:C.red, fontFamily:font.body }}>{error}</span>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit" disabled={loading}
                    style={{ height:48, borderRadius:12, border:'none', cursor: loading ? 'not-allowed' : 'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:15, fontFamily:font.body, display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity: loading ? 0.8 : 1 }}
                  >
                    {loading
                      ? <><Loader size={16} style={{ animation:'spin .7s linear infinite' }}/> Sending…</>
                      : 'Send reset link'
                    }
                  </button>
                </form>
              </>
            ) : (
              <div style={{ textAlign:'center' }}>
                <div style={{ width:60, height:60, borderRadius:'50%', background:'#F0FDF4', border:`2px solid ${C.green}33`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 22px' }}>
                  <Check size={26} color={C.green}/>
                </div>
                <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:24, letterSpacing:'-0.04em', color:C.ink, marginBottom:10 }}>
                  Check your inbox
                </h2>
                <p style={{ fontSize:14, color:C.muted, lineHeight:1.7, marginBottom:28, fontFamily:font.body }}>
                  If <strong style={{ color:C.ink }}>{email}</strong> is registered with Jötr, you'll receive a reset link within a few minutes.
                </p>
                <p style={{ fontSize:12, color:C.muted, marginBottom:24, fontFamily:font.body, lineHeight:1.6 }}>
                  Don't see it? Check your <strong>spam folder</strong>. The link expires after 30 minutes.
                </p>
                <a href="/login" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'11px 24px', borderRadius:10, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:font.body }}>
                  Back to login
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;